import { DataPoint } from "regression";

// Display-only power curve fitting.
// Trusted power points come from the physical QP1 -> QP2/QP2v mapping chain;
// this file only fits and extends those trusted points back to Q = 0.

const EPSILON = 1e-6;
const DENSE_POINT_COUNT = 500;

const clamp = (value: number, min: number, max: number) => {
  return Math.min(max, Math.max(min, value));
};

type TanhFit = {
  intercept: number;
  slope: number;
  amplitude: number;
  shape: number;
  maxQ: number;
};

type PowerCurveFitOptions = {
  anchorPoint?: DataPoint;
};

const sortPoints = (points: DataPoint[] = []) => {
  return [...points].sort((a, b) => a[0] - b[0]);
};

const uniqueByFlow = (points: DataPoint[] = []) => {
  const result: DataPoint[] = [];

  for (const point of sortPoints(points)) {
    const last = result[result.length - 1];

    if (!last || Math.abs(last[0] - point[0]) > EPSILON) {
      result.push([point[0], point[1]] as DataPoint);
      continue;
    }

    result[result.length - 1] = [point[0], point[1]] as DataPoint;
  }

  return result;
};

const selectEffectivePowerPoints = (points: DataPoint[] = []) => {
  return uniqueByFlow(
    sortPoints(points).filter((point) => point[0] > EPSILON && point[1] > EPSILON)
  );
};

type WeightedSample = {
  q: number;
  power: number;
  weight: number;
};

const interpolateY = (points: DataPoint[] = [], q: number) => {
  const sorted = uniqueByFlow(sortPoints(points));

  if (!sorted.length) {
    return undefined;
  }

  if (q <= sorted[0][0]) {
    if (sorted.length < 2) {
      return sorted[0][1];
    }

    const first = sorted[0];
    const second = sorted[1];
    const delta = second[0] - first[0];
    const ratio = delta <= EPSILON ? 0 : (q - first[0]) / delta;

    return Math.max(0, first[1] + (second[1] - first[1]) * ratio);
  }

  const last = sorted[sorted.length - 1];

  if (q >= last[0]) {
    return last[1];
  }

  for (let index = 0; index < sorted.length - 1; index += 1) {
    const current = sorted[index];
    const next = sorted[index + 1];

    if (q >= current[0] && q <= next[0]) {
      const delta = next[0] - current[0];
      const ratio = delta <= EPSILON ? 0 : (q - current[0]) / delta;
      return current[1] + (next[1] - current[1]) * ratio;
    }
  }

  return last[1];
};

const solveLinearSystem3 = (
  matrix: number[][],
  vector: number[]
): [number, number, number] | null => {
  const augmented = matrix.map((row, index) => [...row, vector[index]]);

  for (let pivotIndex = 0; pivotIndex < 3; pivotIndex += 1) {
    let bestRow = pivotIndex;

    for (let rowIndex = pivotIndex + 1; rowIndex < 3; rowIndex += 1) {
      if (
        Math.abs(augmented[rowIndex][pivotIndex]) >
        Math.abs(augmented[bestRow][pivotIndex])
      ) {
        bestRow = rowIndex;
      }
    }

    if (Math.abs(augmented[bestRow][pivotIndex]) <= EPSILON) {
      return null;
    }

    if (bestRow !== pivotIndex) {
      [augmented[pivotIndex], augmented[bestRow]] = [
        augmented[bestRow],
        augmented[pivotIndex],
      ];
    }

    const pivot = augmented[pivotIndex][pivotIndex];

    for (let columnIndex = pivotIndex; columnIndex < 4; columnIndex += 1) {
      augmented[pivotIndex][columnIndex] /= pivot;
    }

    for (let rowIndex = 0; rowIndex < 3; rowIndex += 1) {
      if (rowIndex === pivotIndex) {
        continue;
      }

      const factor = augmented[rowIndex][pivotIndex];

      for (let columnIndex = pivotIndex; columnIndex < 4; columnIndex += 1) {
        augmented[rowIndex][columnIndex] -= factor * augmented[pivotIndex][columnIndex];
      }
    }
  }

  return [
    augmented[0][3],
    augmented[1][3],
    augmented[2][3],
  ];
};

const fitLinearForShape = (
  samples: WeightedSample[],
  shape: number,
  maxQ: number
) => {
  const designSamples = samples.map((sample) => {
    const normalizedQ = maxQ <= EPSILON ? 0 : sample.q / maxQ;
    return {
      linearTerm: normalizedQ,
      tanhTerm: Math.tanh(shape * normalizedQ),
      power: sample.power,
      weight: sample.weight,
    };
  });

  const normalMatrix = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];
  const normalVector = [0, 0, 0];

  for (const sample of designSamples) {
    const features = [1, sample.linearTerm, sample.tanhTerm];
    const weight = sample.weight;

    for (let rowIndex = 0; rowIndex < 3; rowIndex += 1) {
      normalVector[rowIndex] += weight * features[rowIndex] * sample.power;

      for (let columnIndex = 0; columnIndex < 3; columnIndex += 1) {
        normalMatrix[rowIndex][columnIndex] +=
          weight * features[rowIndex] * features[columnIndex];
      }
    }
  }

  const solved = solveLinearSystem3(normalMatrix, normalVector);

  if (!solved) {
    return null;
  }

  const [intercept, slope, amplitude] = solved;
  let error = 0;

  for (const sample of designSamples) {
    const predicted = intercept + slope * sample.linearTerm + amplitude * sample.tanhTerm;
    error += sample.weight * Math.pow(predicted - sample.power, 2);
  }

  return {
    intercept,
    slope,
    amplitude,
    error,
  };
};

const evaluateTanhPower = (fit: TanhFit, q: number) => {
  const normalizedQ = fit.maxQ <= EPSILON ? 0 : q / fit.maxQ;
  return Math.max(
    0,
    fit.intercept +
      fit.slope * normalizedQ +
      fit.amplitude * Math.tanh(fit.shape * normalizedQ)
  );
};

const buildTanhDisplayCurve = (
  points: DataPoint[] = [],
  options: PowerCurveFitOptions = {}
) => {
  const effectivePoints = selectEffectivePowerPoints(points);

  if (effectivePoints.length < 2) {
    return uniqueByFlow(sortPoints(points));
  }

  const fitSamples: WeightedSample[] = effectivePoints.map(([q, power]) => ({
    q,
    power,
    weight: 1,
  }));

  const [anchorQ, anchorP] = options.anchorPoint || [];
  const hasAnchor =
    Number.isFinite(anchorQ) &&
    Number.isFinite(anchorP) &&
    (anchorQ as number) > EPSILON &&
    (anchorP as number) > EPSILON;

  if (hasAnchor) {
    fitSamples.push({
      q: anchorQ as number,
      power: anchorP as number,
      weight: 24,
    });
  }

  const fit = (() => {
    const maxQ = Math.max(
      effectivePoints[effectivePoints.length - 1][0],
      hasAnchor ? (anchorQ as number) : 0
    );

    if (maxQ <= EPSILON) {
      return null;
    }

    let bestFit: TanhFit | null = null;
    let bestError = Number.POSITIVE_INFINITY;

    for (let shape = 0.25; shape <= 10; shape += 0.25) {
      const linearFit = fitLinearForShape(fitSamples, shape, maxQ);

      if (!linearFit) {
        continue;
      }

      if (linearFit.error < bestError) {
        bestError = linearFit.error;
        bestFit = {
          intercept: linearFit.intercept,
          slope: linearFit.slope,
          amplitude: linearFit.amplitude,
          shape,
          maxQ,
        };
      }
    }

    return bestFit;
  })();

  if (!fit) {
    return uniqueByFlow(sortPoints(points));
  }

  if (hasAnchor) {
    const anchorDelta = (anchorP as number) - evaluateTanhPower(fit, anchorQ as number);
    fit.intercept += anchorDelta;
  }

  const maxQ = fit.maxQ;
  const densePoints: DataPoint[] = [];

  for (let index = 0; index <= DENSE_POINT_COUNT; index += 1) {
    const q = (maxQ * index) / DENSE_POINT_COUNT;
    densePoints.push([q, evaluateTanhPower(fit, q)] as DataPoint);
  }

  if (hasAnchor) {
    densePoints.push([anchorQ as number, anchorP as number] as DataPoint);
  }

  return uniqueByFlow(sortPoints(densePoints));
};

const buildMappedEffectivePoints = (
  sourceBaseCurvePoints: DataPoint[] = [],
  targetCurvePoints: DataPoint[] = []
) => {
  const sourceEffective = selectEffectivePowerPoints(sourceBaseCurvePoints);
  const targetEffective = selectEffectivePowerPoints(targetCurvePoints);

  if (sourceEffective.length < 2 || targetEffective.length < 2) {
    return targetEffective;
  }

  const sourceMaxQ = sourceEffective[sourceEffective.length - 1][0];
  const targetMaxQ = targetEffective[targetEffective.length - 1][0];

  if (sourceMaxQ <= EPSILON || targetMaxQ <= EPSILON) {
    return targetEffective;
  }

  const sourceSpan = Math.max(EPSILON, sourceMaxQ);
  const targetSpan = Math.max(EPSILON, targetMaxQ);

  const mappedTrustedPoints = sourceEffective
    .map((point) => {
      const ratio = clamp(point[0] / sourceSpan, 0, 1);
      const mappedQ = ratio * targetSpan;
      const mappedP = interpolateY(targetEffective, mappedQ);

      if (!Number.isFinite(mappedP) || mappedP === undefined) {
        return null;
      }

      return [mappedQ, Math.max(0, mappedP)] as DataPoint;
    })
    .filter((point): point is DataPoint => Boolean(point));

  if (mappedTrustedPoints.length < 2) {
    return targetEffective;
  }

  return uniqueByFlow(sortPoints(mappedTrustedPoints));
};

export const buildBasePowerDisplayCurve = (points: DataPoint[] = []) => {
  return buildTanhDisplayCurve(buildMappedEffectivePoints(points, points));
};

export const buildExtendedPowerDisplayCurve = (
  sourceBaseCurvePoints: DataPoint[] = [],
  targetCurvePoints: DataPoint[] = [],
  options: PowerCurveFitOptions = {}
) => {
  const mappedEffectivePoints = buildMappedEffectivePoints(
    sourceBaseCurvePoints,
    targetCurvePoints
  );

  return buildTanhDisplayCurve(mappedEffectivePoints, options);
};
